// src/components/Apm/Profile/CompanyProfile.js

import { useCallback } from 'react';

import { toast } from 'react-toastify';

import { useTranslation } from 'react-i18next';

import {
  Avatar,
  Box,
  Card,
  CardActions,
  CardContent,
  CardHeader,
  CircularProgress,
  Divider,
  Grid,
  Typography,
} from '@mui/material';

import AnchorIcon from '@mui/icons-material/Anchor';

import { useUpdateCurrentCompanyMutation } from '../../../redux/features/companyApi';

import { useGetImageQuery } from '../../../redux/features/imageApi';

import MultipleFileUploadField, { COMPANY_IMAGE_MAX_FILE_SIZE, IMAGE_ACCEPTED_FILE_TYPES } from '../../Upload/MultipleFileUploadField';

import ButtonLoading from '../../Controls/Buttons/ButtonLoading';

export const CompanyProfile = ({ company, canEdit }) => {
  const { t } = useTranslation();

  const logoId = company?.logo ?? null;

  const { data: logoResponse, isFetching: isLogoFetching } = useGetImageQuery(logoId ? String(logoId) : '', {
    skip: !logoId,
  });

  const logo = logoResponse?.s3_url ?? null;

  const [updateCurrentCompany, { isLoading: isUpdatingCompany }] = useUpdateCurrentCompanyMutation();

  const putCompanyLogo = useCallback(
    async (imageId) => {
      if (!canEdit) {
        toast.error(
          t('toast.only_account_manager_allowed', {
            defaultValue: 'You are not allowed to edit this company.',
          })
        );

        return null;
      }

      try {
        const updatedContext = await updateCurrentCompany({
          logo: imageId || null,
        }).unwrap();

        toast.success(
          t('toast.logo_successfully_updated', {
            defaultValue: 'Logo successfully updated.',
          })
        );

        return updatedContext;
      } catch (error) {
        console.error('Company logo update failed:', error);

        toast.error(
          t('toast.logo_could_not_be_updated', {
            defaultValue: 'Logo could not be updated.',
          })
        );

        return null;
      }
    },
    [canEdit, t, updateCurrentCompany]
  );

  const deleteLogo = async (event) => {
    event.preventDefault();

    if (!canEdit || !logoId) {
      return;
    }

    try {
      await updateCurrentCompany({
        logo: null,
      }).unwrap();

      toast.success(
        t('toast.logo_successfully_deleted', {
          defaultValue: 'Logo successfully deleted.',
        })
      );
    } catch (error) {
      console.error('Company logo deletion failed:', error);

      toast.error(
        t('toast.logo_could_not_be_deleted', {
          defaultValue: 'Logo could not be deleted.',
        })
      );
    }
  };

  return (
    <Card
      sx={{
        width: '100%',
        height: '100%',
      }}
    >
      <CardHeader
        title={<Typography variant="h6">{t('company.company_logo')}</Typography>}
        sx={{
          backgroundColor: '#f1f1f1',
        }}
      />

      <Divider />

      <CardContent>
        <Box
          sx={{
            display: 'flex',
            alignItems: 'center',
            flexDirection: 'column',
            justifyContent: 'center',
            height: 145,
            mt: 0.5,
          }}
        >
          {isLogoFetching ? (
            <CircularProgress size={32} />
          ) : (
            <Avatar
              src={logo || undefined}
              sx={{
                width: logo ? 'auto' : 113.75,
                minHeight: 105,
                minWidth: 113.75,
                mb: 2,
                objectFit: 'contain',
              }}
              imgProps={{
                style: {
                  objectFit: 'contain',
                },
              }}
              variant="square"
            >
              <AnchorIcon fontSize="large" />
            </Avatar>
          )}
        </Box>
      </CardContent>

      {canEdit && (
        <>
          <Divider />

          <CardActions>
            <MultipleFileUploadField
              variant="company-logo"
              currentFileId={logoId}
              acceptedFileTypes={IMAGE_ACCEPTED_FILE_TYPES}
              maxFileSize={COMPANY_IMAGE_MAX_FILE_SIZE}
              disabled={isUpdatingCompany}
              onUploaded={putCompanyLogo}
            />
          </CardActions>

          <Divider />

          <CardActions
            sx={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'column',
            }}
          >
            <Grid
              container
              item
              sx={{
                width: '100%',
              }}
            >
              <ButtonLoading
                type="button"
                variant="outlined"
                color="secondary"
                onClick={deleteLogo}
                loading={isUpdatingCompany}
                disabled={!logoId || isUpdatingCompany}
                fullWidth
                sx={{
                  mx: 0,
                  my: 1,
                }}
              >
                <Typography variant="button">{t('button.delete_logo')}</Typography>
              </ButtonLoading>
            </Grid>
          </CardActions>
        </>
      )}
    </Card>
  );
};
